import { NavItem } from 'epubjs/types/navigation';
import type { EnhancedTocItem } from '../types/navigation';

/**
 * Compares a NavItem's position relative to a selectedHref using a predicate function.
 * Returns:
 * -1 if selectedHref appears before the predicate item
 *  0 if it's the same item
 *  1 if it appears after
 *  null if not matched in this branch
 */
const compareItemPosition = (
  item: EnhancedTocItem,
  selectedHref: string,
  predicate: (item: EnhancedTocItem) => boolean,
): number | null => {
  if (predicate(item)) {
    return selectedHref === item.href ? 0 : 1;
  }
  if (selectedHref === item.href) {
    return -1;
  }
  return null;
};

/**
 * Recursively compares selectedHref's position within the TOC hierarchy.
 */
export const compareItemPositionInToc = (
  items: EnhancedTocItem[],
  selectedHref: string,
  predicate: (item: EnhancedTocItem) => boolean,
): number | null => {
  for (const item of items) {
    const result = compareItemPosition(item, selectedHref, predicate);
    if (result !== null) return result;

    const childResult = compareItemPositionInToc(item.subitems || [], selectedHref, predicate);
    if (childResult !== null) return childResult;
  }
  return null;
};

/**
 * Recursively flattens a hierarchical TOC structure into a single array of NavItems
 */
export const flattenToc = (toc: NavItem[]): NavItem[] => {
  const flat: NavItem[] = [];

  const walk = (items: NavItem[]) => {
    for (const item of items) {
      flat.push(item);
      if (item.subitems?.length) {
        walk(item.subitems);
      }
    }
  };

  walk(toc);
  return flat;
};

const tocUtil = {
  compareItemPositionInToc,
  flattenToc,
};

export default tocUtil;
